<script setup>
import Dropzone from 'dropzone';
import Sortable from 'sortablejs';
import { createTooltip } from 'floating-vue';
import {
  inject, onMounted, onUnmounted, ref,
} from 'vue';
import toast from '../libs/cifra/toast';
import { getCsrfInfo } from '../libs/http';
import api, { getUrlIconoNoImagen } from '../api/cifra/libraryable';

// import 'dropzone/dist/basic.css';
// import 'dropzone/dist/dropzone.css';

const PREVIEW_TEMPLATE = `
  <div class="col-md-2 dz-preview dz-file-preview">
    <div class="thumbnail dz-details"><img data-dz-thumbnail>
      <div class="caption">
        <h5 data-dz-name></h5>
        <p class="dz-size" data-dz-size></p>
        <div class="dz-success-mark"><span class="glyphicon glyphicon-ok text-success"></span></div>
        <div class="dz-error-mark"><span class="glyphicon glyphicon-warning-sign text-danger"></span></div>
        <div class="progress dz-progress">
          <div class="progress-bar progress-bar-striped dz-upload" role="progressbar" aria-valuemin="0"
            aria-valuemax="100" data-dz-uploadprogress><span class="sr-only"></span></div>
        </div>
        <div class="alert alert-danger dz-error-message hide" role="alert" data-dz-errormessage></div>
      </div>
      <div class="overlay"><i class="fa fa-external-link"></i></div>
    </div>
  </div>`;

const I18n = inject('I18n');

const props = defineProps({
  modelo: { type: String, required: true },
  asociacion: { type: String, default: null },
  idModelo: { type: Number, default: null },
  paramName: { type: String, default: 'archivo' },
  ordenable: { type: Boolean, default: false },
  disabled: { type: Boolean, default: false },
  // Tamaño máximo de los adjuntos en megas
  maxFilesize: { type: Number, default: 10 },
  maxFiles: { type: Number, default: 10 },
  // Tamaños máximos a los que se redimensionará la imagen
  resizeWidth: { type: Number, default: 1920 },
  resizeHeight: { type: Number, default: 1080 },
});

const dropzoneRoot = ref();
let dropzone;

function ficheroSubidoCorrectamente(file, response) {
  file.modelo = response.file_model;
  file.idModelo = response.file_id;

  file.previewElement.dataset.idModelo = file.idModelo;
  file.previewElement.addEventListener('click', () => {
    // En los cargados inicialmente no sé por qué está en path en vez de file_url...
    window.open(response.file_url || response.path, '_blank');
  });
  if (props.ordenable) {
    const botonMover = document.createRange().createContextualFragment(
      `<div class="dz-move btn btn-default btn-xs" title="${I18n.t('Ordenar')}"><i class="fa fa-arrows-alt"></i></div>`,
    );
    file.previewElement.appendChild(botonMover);
  }
}

function eliminarFichero(file) {
  if (props.disabled) {
    return;
  }
  if (file.idModelo && file.modelo) {
    api.destroy(file.modelo, file.idModelo, file.asociacion).catch((error) => {
      const mensaje = error.response.data?.error || error.message;
      toast.error(mensaje);
    });
  }
}

let sortable;
function conectarOrdenable() {
  if (props.disabled) {
    return;
  }

  sortable = Sortable.create(dropzoneRoot.value, {
    handle: '.dz-move',
    animation: 150,
    onUpdate: (event) => {
      if (props.disabled) {
        return;
      }

      const listaIdsNuevos = Array.from(event.to.querySelectorAll(':scope .dz-preview')).map((col) => (
        col.dataset.idModelo
      ));
      api.ordenar(props.modelo, props.idModelo, listaIdsNuevos);
    },
  });
}

function cargarAdjuntosActuales() {
  api.getArchivosActuales(props.modelo, props.idModelo, props.asociacion).then((result) => {
    const ficheros = result.data;
    ficheros.forEach((fichero) => {
      let thumbUrl = fichero.thumbnail;
      if (!thumbUrl) {
        thumbUrl = getUrlIconoNoImagen(fichero.content_type);
      }
      let { name } = fichero;
      if (fichero.file_type) {
        name = I18n.t(`enum_tipo_archivo.${fichero.file_type}`);
      }
      const mock = {
        name,
        size: fichero.size,
        type: fichero.content_type,
        imageUrl: thumbUrl,
        // Si no indicamos esto no funciona el parámetro maxFiles
        accepted: true,
      };

      dropzone.displayExistingFile(
        mock,
        thumbUrl,
        () => {
          mock.previewElement.classList.add('dz-server-file');
        },
        null,
        // Este false es para que no haga el thumbnail, nos da un error de CORS al intentar
        // acceder al contenido de la imagen pra hacer el thumbnail
        false,
      );

      // Para que inicialice como si se hubiera subido el fichero
      ficheroSubidoCorrectamente(mock, fichero);

      // Necesario para que se inicialice bien el plugin y contabilice los elementos añadidos
      dropzone.files.push(mock);
    });

    // Para que refresque una vez que hemos añadido los elementos
    if (props.ordenable) {
      conectarOrdenable();
    }
  });
}

// Lanza un evento vanilla js en el elemento dropzoneRoot
function lanzarEventoElemento(nombre, detail = {}) {
  const event = new CustomEvent(
    nombre,
    { detail },
  );

  dropzoneRoot.value.dispatchEvent(event);
}

onMounted(() => {
  let { maxFilesize } = props;

  // Si hay redimensionamiento no controlamos el máximo porque puede que
  // seleccionen una foto de 10 megas pero al redimensionar sean 2. Si tenemos
  // el límite en 3, da error, ni siquiera redimensionaría
  if (props.resizeWidth || props.resizeHeight) {
    maxFilesize = null;
  }

  dropzone = new Dropzone(
    dropzoneRoot.value,
    {
      previewTemplate: PREVIEW_TEMPLATE,
      url: () => (props.disabled ? '/invalid' : api.getUrlUpload(props.modelo, props.idModelo, props.asociacion)),
      paramName: props.paramName,
      withCredentials: true,
      addRemoveLinks: true,
      clickable: !props.disabled,
      // Nos aseguramos de que no acepta nada por si arrastran
      acceptedFiles: props.disabled ? 'fakemime' : null,
      maxFilesize,
      maxFiles: props.maxFiles,
      resizeWidth: props.resizeWidth,
      resizeHeight: props.resizeHeight,
      dictFallbackMessage: '',
      dictFallbackText: null,
      dictInvalidFileType: I18n.t('dz_dict_invalid_file_type'),
      dictFileTooBig: I18n.t('dz_dict_file_too_big'),
      dictResponseError: I18n.t('dz_error_subiendo_archivo'),
      dictRemoveFile: `<div class="btn btn-danger btn-xs pull-right" title="${I18n.t('Eliminar')}"><i class="fa fa-trash"></i></div>`,
      dictMaxFilesExceeded: I18n.t('dz_dict_max_files_exceeded'),
      dictRemoveFileConfirmation: I18n.t('confirm_eliminar_adjunto'),
      headers: {
        'X-CSRF-Token': getCsrfInfo().token,
      },
    },
  );
  dropzone.on('addedfile', (file) => {
    if (!file.type.match(/image.*/)) {
      const thumb = getUrlIconoNoImagen(file.type);
      dropzone.emit('thumbnail', file, thumb);
    }
    lanzarEventoElemento('addedfile', { file });
  });

  dropzone.on('success', (file, response) => {
    ficheroSubidoCorrectamente(file, response);
  });

  dropzone.on('removedfile', (file) => {
    eliminarFichero(file);
  });

  dropzone.on('error', (file, message) => {
    const el = file.previewElement;
    if (!el) {
      return;
    }
    const elErrorMark = el.querySelector('.dz-error-mark');
    let contenido = message;
    if (typeof message !== 'string' && message.error) {
      contenido = message.error;
    }
    createTooltip(elErrorMark, contenido);
  });

  dropzone.on('complete', (file) => {
    lanzarEventoElemento('complete', { file });
  });

  cargarAdjuntosActuales();
  if (props.ordenable) {
    conectarOrdenable();
  }
});

onUnmounted(() => {
  if (sortable) {
    sortable.destroy();
  }
  if (dropzone) {
    // En la documentación pone que esto no está testeado...
    // Ojo que no vale!!!!! que llama a removedfile y se borran
    // todas las fotos del servidor
    // dropzone.destroy();
  }
});

</script>

<template>
  <div class="dropzone-container">
    <div ref="dropzoneRoot" class="dropzone-cifra dropzone dz-clickable" :class="{ disabled }">
      <div class="dz-default dz-message">
        <i class="fa fa-paperclip"></i>
        {{ disabled ? $t('dz_dict_default_message_disabled') : $t('dz_dict_default_message') }}
      </div>
    </div>
  </div>
</template>

<style lang="scss">
// Este CSS está traído del código viejo y modificado para adaptarlo. No sé si
// sobran cosas o lo que sea
@import "@/../assets/stylesheets/cifra/vars/main.scss";
@import "@/../assets/stylesheets/cifra/mixins/size.scss";
@import "@/../assets/stylesheets/cifra/mixins/position.scss";

.dropzone {
  border: 5px dotted #ddd;
  cursor: pointer;
  margin-bottom: 20px;
  padding: 15px 0;
  position: relative;

  &.has-error {
    border: 2px dotted #ed5565;
  }

  .dz-message {
    color: #a6a6a6;
    text-align: center;
    font-weight: 500;

    .fa {
      color: #ddd;
      display: block;
      font-size: 120px;
    }
  }

  &.dz-started {
    .dz-message {
      display: none;
    }
  }

  &.dz-drag-hover {
    background: #f0f0f0;
    border: 5px dotted #ddd;
  }

  .dz-image-preview {
    display: inline-block;
    float: none;
    vertical-align: top;

    &.dz-uploaded {
      .dz-progress {
        display: none;
      }

      .dz-details {
        padding-bottom: 15px;
      }
    }

    .dz-move {
      float:left;
    }

    &:not(.dz-error):hover {
      .dz-download,
      .dz-move,
      .dz-remove {
        visibility: visible;
      }

      .overlay {
        @include position-absolute(5px, 4px, 4px, 4px);
        background: #000;
        display: block;
        opacity: 0.5;

        .fa {
          @include size(36px, 36px);
          @include position-absolute(40%, null, null, 50%);
          color: #fff;
          display: block;
          font-size: 36px;
          margin: -18px 0 0 -18px;
        }
      }
    }
  }

  .dz-details {
    margin-bottom: 0;
    position: relative;
  }

  .dz-size {
    float: left;
  }

  .dz-success {
    .dz-success-mark {
      display: block;
    }
  }

  .dz-error {
    .dz-error-mark {
      display: block;
    }
  }

  .dz-success-mark {
    display: none;
    float: right;

    span {
      font-size: 20px;
      margin-top: -2px;
    }
  }

  .dz-error-mark {
    display: none;
    float: right;

    span {
      font-size: 20px;
      margin-top: -2px;
    }
  }

  .dz-progress {
    clear: both;
    margin-bottom: 0;
  }

  .caption {
    padding-top: 0;
    text-align: center;

    h5 {
      word-wrap: break-word;
    }
  }

  .dz-download,
  .dz-move,
  .dz-remove {
    display: inline;
    float: right;
    margin-left: 2px;
    margin-top: 2px;
    visibility: hidden;
  }

  .overlay {
    display: none;
  }

  .glyphicon-move {
    display: none;
  }

  .dz-preview.dz-image-preview [data-dz-thumbnail] {
    display: block;
    min-height: 128px;
    max-height: 128px;
    -o-object-fit: cover;
    object-fit: cover;
    overflow: hidden;
  }

  .sm-file .dropzone {
    .caption {
      display: inline-block;
    }

    .dz-message {
      span {
        font-size: 12px;
      }
    }

    .dz-download,
    .dz-remove {
      display: inline;
      margin-left: 2px;
      margin-top: 2px;
      position: absolute;
      right: 15px;
      top: 0;
      visibility: hidden;
    }
  }

  .dz-server-file {
    .dz-progress {
      visibility: hidden;
    }
  }

  &.disabled {
    background-color: $light-gray;

    .dz-image-preview {
      .dz-download,
      .dz-move,
      .dz-remove {
        visibility: hidden !important;
      }
    }
  }
}
</style>
